/**
 * Common JS controller.
 *
 * Use this class to run scripts globally and to provide
 * modular helper functions for other scripts classes.
 */

// Use jQuery as $ within this file scope.
const $ = jQuery;

/**
 * Class Common
 */
export default class Common {

    /**
     * Class constructor is for binding class properties.
     */
    constructor() {
        // Bind this.
        Common.setCookie = Common.setCookie.bind( this );
        Common.getFavoritedHouses = Common.getFavoritedHouses.bind( this );
        Common.updateFavoriteButtonsList = Common.updateFavoriteButtonsList.bind( this );
        Common.showPrices = Common.showPrices.bind( this );
        Common.getPriceContainers = Common.getPriceContainers.bind( this );
    }

    /**
     * Cache dom elements for use in the class's methods
     */
    cache() {

        // The accessible outline style container.
        this.focusStyleContainer = $( '#accessible-outline' );

        // Scan all object-fit-containers on the page.
        this.objectfitContainers = $( '.js-object-fit-image-container' );

        // Assign back to top-element to variable
        this.scrollButton = $( '#js-back-to-top' );

        // Cookie disclaimer
        this.cookieStorageButton = $( '#js-cookie-notice-button' );
        this.cookieDisclaimer    = $( '#js-cookie-notice' );

        // Last opened element so we know which one to close
        this.openContentElements = {
            prevContent: null,
            prevTarget: null,
            prevButton: null
        };

        // Favorite buttons.
        this.favoriteButtons = document.querySelectorAll( '.js-favorite' );

        // Favorites count container in header.
        this.favoritesCountContainer = $( '#js-favorites-count-container' );

        // Favorites count number in header.
        this.favoritesCount = $( '#js-favorites-count-number' );

        // All Gravity Forms select fields.
        this.formSelects = $( '.gform_wrapper select' );

        // Captions slider fancy box.
        this.$fancyBoxCaptionsLayout  = $( '.js-fancy-box-group-captions' );
    }

    /**
     * All common events.
     *
     * @return {void}
     */
    events() {

        // Missing forEach on NodeList for IE11.
        if ( window.NodeList && ! NodeList.prototype.forEach ) {
            NodeList.prototype.forEach = Array.prototype.forEach;
        }

        // Add wrapper to select fields.
        this.formSelects.each( function() {
            $( this ).wrap( '<div class="styled-select"></div>' );
        });

        // Add the outline nullifying style to the
        // container in the document head when using the mouse.
        document.body.addEventListener( 'mousedown', e => {
            this.nullifyOutline();
        });

        // Add the outline nullifying style to the
        // container in the document head on touchevents.
        document.body.addEventListener( 'touchstart', e => {
            this.nullifyOutline();
        });

        // Remove the outline nullifying style from the
        // container in the document head when using the keyboard.
        document.body.addEventListener( 'keydown', e => {
            if ( this.focusStyleContainer ) {
                this.focusStyleContainer.innerHTML = '';
            }
        });

        // Close cookie disclaimer + set local storage info.
        this.cookieStorageButton.on( 'click', e => {
            localStorage.setItem( 'cookieDisclaimerClicked', 1 );
            this.cookieDisclaimer.addClass( 'js-hidden' );
        });
    }

    /**
     * Initialize fancy box for captions slider.
     */
    initCaptionsFancyBox() {

        this.$fancyBoxCaptionsLayout.fancybox({
            hash: false,
            infobar: true,
            buttons: [
                'close'
            ],
            baseTpl:
                '<div class="fancybox-container" role="dialog" tabindex="-1">' +
                '<div class="fancybox-bg"></div>' +
                '<div class="fancybox-inner">' +
                '<div class="fancybox-toolbar">{{buttons}}</div>' +
                '<div class="fancybox-stage"></div>' +
                '<div class="fancybox-infobar"><strong>Kuva</strong> <span data-fancybox-index></span>&nbsp;/&nbsp;<span data-fancybox-count></span></div>' +
                '<div class="fancybox-navigation">{{arrows}}</div>' +
                '</div>' +
                '</div>',
            afterLoad: function( instance, current ) {

                const externalLink = current.opts.url;

                if ( typeof externalLink !== 'undefined' ) {
                    $( current.$content ).wrapInner( '<a href="' + externalLink + '" target="_blank" rel="noopener noreferrer"></a>' );

                    $( current.$image ).after( '<span class="external-link-icon"></span>' );
                }

                if ( current.opts.caption ) {
                    current.$content.append( '<div class="fancybox-custom-caption">' + current.opts.caption + '</div>' );
                }
            }
        });
    }

    /**
     * Update favorite buttons list.
     *
     * @param {object} buttons A NodeList.
     * @return {void}
     */
    static updateFavoriteButtonsList( buttons ) {

        // If parameter is empty get all favorite buttons from page.
        if ( buttons == null ) {
            buttons = document.querySelectorAll( '.js-favorite' );
        }

        this.favoriteButtonsEvents( buttons );
    }

    /**
     * Favorite buttons events.
     *
     * @param {object} buttons A NodeList.
     * @return {void}
     */
    favoriteButtonsEvents( buttons ) {

        // Favorited house IDs.
        const favoritedIds = Common.getFavoritedHouses();

        buttons.forEach( ( button ) => {
            const id = button.getAttribute( 'data-id' );

            if ( favoritedIds.includes( id ) ) {
                button.classList.add( 'remove-from-favorites' );
                button.classList.remove( 'add-to-favorites' );
            }

            /**
             * Add house to favorites.
             **/
            button.addEventListener( 'click', ( e ) => {

                let target = e.target;

                // If clicked span element inside a button.
                if ( target.tagName === 'SPAN' ) {
                    target = e.target.parentNode;
                }

                // House's ID.
                const houseId = target.getAttribute( 'data-id' );

                // Change class from all other "same" favorite buttons.
                this.favoriteButtons.forEach( ( button ) => {
                    const id = button.getAttribute( 'data-id' );

                    if ( id === houseId && button !== target ) {

                        // Usually using toggleClass is not the best option. But in this case it is OK.
                         $( button ).toggleClass( 'remove-from-favorites' );
                         $( button ).toggleClass( 'add-to-favorites' );
                     }
                 });

                if ( target.classList.contains( 'add-to-favorites' ) ) {

                    // Store house's ID inside a cookie.
                    Common.setCookie( 'hirsitalot-favorites[' + houseId + ']', houseId, null );

                    target.classList.remove( 'add-to-favorites' );
                    target.classList.add( 'remove-from-favorites' );

                } else if ( target.classList.contains( 'remove-from-favorites' ) ) {

                    // Remove cookie.
                    Common.setCookie( 'hirsitalot-favorites[' + houseId + ']', houseId, 1 );

                    target.classList.add( 'add-to-favorites' );
                    target.classList.remove( 'remove-from-favorites' );
                }
            });
        });
    }

    /**
     * Get favorited houses from cookies.
     *
     * @return {array}
     */
    static getFavoritedHouses() {

        // All cookies.
        const cookies = document.cookie;
        const cookieArray = cookies.split( ';' );

        // Empty array for cookie names.
        const cookieNames = [];

        // Get all cookie names.
        cookieArray.forEach( ( cookie ) => {
            cookieNames.push( cookie.trim() );
        });

        // Get cookies which start with 'hirsitalot-favorites'.
        const favorites = cookieNames.filter( ( cookie ) => {
            return cookie.startsWith( 'hirsitalot-favorites' );
        });

        // Empty array for favorited house IDs.
        const favoritesIds = [];

        // Get IDs.
        favorites.forEach( ( favorite ) => {
            favoritesIds.push( favorite.substr( favorite.indexOf( '=' ) + 1 ) );
        });

        // Return an array of house IDs.
        return favoritesIds;
    }

    /**
     * Update favorites count number in header via AJAX.
     *
     * @param {array} favorites
     * @return {void}
     */
    updateFavoritesCount( favorites ) {

        // Hide count number container if numer is 0.
        if ( favorites.length === 0 ) {
            this.favoritesCountContainer.addClass( 'hidden' );
        } else {
            this.favoritesCountContainer.removeClass( 'hidden' );
            this.favoritesCount.html( favorites.length );
        }
    }

    /**
     * Add nullifying outline CSS rules to focusStyleContainer.
     *
     * @return {void}
     */
    nullifyOutline() {
        if ( this.focusStyleContainer ) {
            this.focusStyleContainer.innerHTML = '*:focus{outline:none!important;}';
        }
    }

    /**
     * Fallback for object fit.
     *
     * @return {void}
     */
    objectFitFallback() {

        // IE and Edge CSS Object fit fallback javascript
        if ( ! Modernizr.objectfit ) {

            // Loop through object fit containers.
            this.objectfitContainers.each( function() {

                // Variables
                const $container = $( this );
                const imgUrl     = $container.find( 'img' ).prop( 'src' );

                if ( imgUrl ) {
                    $container
                    .css( 'backgroundImage', 'url(' + imgUrl + ')' )
                    .addClass( 'compat-object-fit' );
                }
            });
        } // End if().
    }

    /**
     * Scroll back to the top of the page.
     *
     * @return {void}
     */
    backToTop() {

        // Show back to top button when scrolling.
        $( window ).on( 'scroll', () => {
            const scrollTop = $( window ).scrollTop();
            const scrollTrigger = 500; // px

            if ( scrollTop > scrollTrigger ) {
                this.scrollButton.addClass( 'show' );
            } else {
                this.scrollButton.removeClass( 'show' );
            }
        });

        // When the user clicks on the button, scroll to the top of the document.
        this.scrollButton.on( 'click', ( e ) => {
            e.preventDefault();
            $( 'html, body' ).animate({
                scrollTop: 0
            }, 300 );
        });
    }

    /**
      * Hide Cookie disclaimer if closed before.
      *
      * @return {void}
      */
     cookieDisclaimerCheck() {

        const cookieDisclaimerClicked = localStorage.getItem( 'cookieDisclaimerClicked' );

        if ( cookieDisclaimerClicked == 1 ) {
            this.cookieDisclaimer.addClass( 'js-hidden' );
        }
     }

    /**
     * Toggle language select dropdown elements.
     *
     * @return {void}
     */
    toggleLanguages() {
        $( '.js-language-select' ).on( 'click', '.init', function() {
            $( this ).closest( 'ul' ).children( 'li:not(.init)' ).toggle();
            $( '.js-arrow-icon' ).toggleClass( 'arrow-icon-active' );
        });
    }

    /**
     * Get all house price containers from page.
     *
     * @return {array}
     */
    static getPriceContainers() {

        // Empty array for house IDs.
        const houseIds = [];

        // All price containers.
        const priceContainers = $( '.js-price' );

        priceContainers.each( ( index, priceContainer ) => {
            houseIds.push( priceContainer.getAttribute( 'data-id' ) );
        });

        return houseIds;
    }

    /**
     * Set location cookie.
     *
     * @return {void}
     */
    static setLocationCookie() {

        // Set cookie with AJAX.
        dp( 'ThemeBase/SetCountryCookie', {
        }).then( ( response ) => {

            // Show prices after setting cookie.
            if ( response.success.ThemeBase.SetCountryCookie === 'success' ) {
                Common.showPrices();
            }
        }).catch( ( error ) => {
            console.log( error );
        });
    }

    /**
     * Show house's prices if visitors IP comes from Finland.
     *
     * @return {void}
     */
    static showPrices() {

        // Go through each price container and get house's ID from data-attribute.
        const houseIds = Common.getPriceContainers();

        // Assign this to thisClass to pass inside success.
        const thisClass = this;

        // Show prices with AJAX.
        dp( 'ThemeBase/ShowPrices', {
            args: {
                /*eslint-disable */
                ajax_params: {
                    houseIds: houseIds,
                }
                /*eslint-enable */
            }
        }).then( ( response ) => {

            // Data from model.
            const prices = response.success.ThemeBase.ShowPrices;

            if ( typeof prices !== 'undefined' ) {

                $( '.price-container-text' ).removeClass( 'hidden' );

                // All price containers.
                const priceContainers = $( '.js-price' );

                // Go trough each price container.
                priceContainers.each( ( index, priceContainer ) => {

                    // Get houses's ID from data-attribute.
                    const dataId = priceContainer.getAttribute( 'data-id' );

                    // Populate price container.
                    const value = prices[dataId];
                    priceContainer.innerHTML = value + '&nbsp;€';
                });
            }
        }).catch( ( error ) => {
            console.log( error );
        });
    }

    /**
     * Get cookie's value by name.
     *
     * @param {string} name Cookie's name.
     * @return {string|boolen}
     */
    static getCookieByName( name ) {
        const pattern = RegExp( name + '=.[^;]*' );
        const matched = document.cookie.match( pattern );

        if ( matched ) {
            const cookie = matched[0].split( '=' );
            return cookie[1];
        }

        return false;
    }

    /**
     * Create a cookie via AJAX.
     *
     * @param {string} cookieName
     * @param {string} cookieValue
     * @param {string} cookieExpires
     * @return {void}
     */
    static setCookie( cookieName, cookieValue, cookieExpires ) {

        dp( 'PageFavorites/SetCookie', {
            args: {
                /*eslint-disable */
                ajax_params: {
                    cookieName: cookieName,
                    cookieValue: cookieValue,
                    cookieExpires: cookieExpires,
                }
                /*eslint-enable */
            }
        }).then( ( response ) => {

            // Update favorites count number in header.
            this.updateFavoritesCount( Common.getFavoritedHouses() );
        }).catch( ( error ) => {
            console.log( error );
        });
    }

    /**
     * Hide /show hero videos based on cookie consent.
     */
    toggleHeroVideo( show ) {

        if ( ! $( '.hero-iframe' ).length ) {
            return;
        }
        
        // Add the src from data-src
        if( show === true ) {
            $('.hero-iframe').each(function() {
                var $this = $(this);
                $this.attr('src', $this.data('src'));
            });

            return;
        }

        // Remove src
        $('.hero-iframe').each(function() {
            var $this = $(this);
            $this.removeAttr('src');
        });
    }

    /**
     * Run when the document is ready.
     *
     * @return {void}
     */
    docReady() {
        this.cache();
        this.events();
        this.objectFitFallback();
        this.backToTop();
        this.cookieDisclaimerCheck();
        this.toggleLanguages();
        this.favoriteButtonsEvents( this.favoriteButtons );
        Common.getFavoritedHouses();
        this.updateFavoritesCount( Common.getFavoritedHouses() );
        Common.setLocationCookie();
        Common.showPrices();
        this.initCaptionsFancyBox();

        window.addEventListener('CookiebotOnConsentReady', () => {
            this.toggleHeroVideo(Cookiebot.consent.statistics);
        }, false);
    }


    /**
     * Select a list of matching child elements from an array of elements.
     *
     * @param  {object} elements A nodelist. Use each item in nodelist as context when finding the children.
     * @param  {string} selector The query selector string.
     * @return {object}   Returns empty array if no matches are found; otherwise, it returns all matching elements.
     */
    static childrenFromEach( elements, selector ) {

        // Return empty array if missing required attributes
        if ( ! elements || ! selector ) {
            return [];
        }

        const arrayOfNodes = Array.prototype.map.call( elements,
            function( element ) {

                // Get all matching elements.
                const matchingChildren = ( element ).querySelectorAll( selector );

                // Convert nodelist to array.
                return [ ...matchingChildren ];
            }
        );

        // Flatten the array
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/Reduce?v=a
        if ( arrayOfNodes.length !== 0 ) {
            const flattened = arrayOfNodes.reduce( ( a, b ) => [ ...a, ...b ]);
            return flattened;
        }
    }

    /**
     * Offers safe way to stop a JS event.
     *
     * Example usage:
     * Theme.Common.stop(e);
     *
     * @param e Event object.
     */
    static stop( e ) {
        e.preventDefault ? e.preventDefault() : ( e.returnValue = false );
    }

    /**
     * This gets a URL parameter according to given parameter string.
     *
     * @param {string} name
     */
    static getUrlParameter( name ) {
        name          = name.replace( /[\[]/, '\\[' ).replace( /[\]]/, '\\]' );
        const regex   = new RegExp( '[\\?&]' + name + '=([^&#]*)' );
        const results = regex.exec( location.search );

        return results === null ? false : decodeURIComponent( results[1].replace( /\+/g, ' ' ) );
    };

    /**
     * This is a custom empty check for parameters.
     *
     * @param {mixed} param The given parameter
     */
    static empty( param ) {
        if ( typeof param == undefined || param == '' || param == null || param == false || param.length == 0 ) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Open hidden content, used with cmd-attributes
     *
     * @param {event} e
     */
    openTab( e ) {
        const targetItem = $( e.target ).closest( '.footer__card' ).get()[0];
        const content = $( targetItem ).find( '.footer__card-content' ).get()[0];
        const button = $( targetItem ).find( '.footer__button-wrapper' ).get()[0];
        const openedClass = 'footer__card--opened';
        const contentVisible = 'content-visible-sm';

        const {
            prevContent,
            prevTarget,
            prevButton
        } = this.openContentElements;

        // If previously opened item exists
        if ( prevTarget ) {
            prevContent.classList.remove( contentVisible );
            prevTarget.classList.remove( openedClass );
            prevButton.setAttribute( 'aria-expanded', false );
        }

        // If previously opened is same as clicked, only close it, otherwise open it
        if ( prevTarget === targetItem ) {
            this.openContentElements =  {
                prevContent: null,
                prevTarget: null,
                prevButton: null
            };
        } else {
            targetItem.classList.add( openedClass );
            button.setAttribute( 'aria-expanded', true );
            this.openContentElements = {
                prevContent: content,
                prevTarget: targetItem,
                prevButton: button
            };
            content.classList.add( contentVisible );
        }
    }
}



// WEBPACK FOOTER //
// ./assets/scripts/common.js